import { lazy, type ComponentType } from 'react';

/**
 * ``React.lazy`` that survives a deploy.
 *
 * Route chunks are content-hashed, and Cloudflare Pages serves only the newest
 * deployment's assets. A tab still running the previous bundle therefore asks
 * for a hashed filename that no longer exists the moment it navigates to a
 * lazy route — the dynamic import rejects with "Failed to fetch dynamically
 * imported module" and the click surfaces as an ErrorBoundary crash.
 *
 * ``main.tsx`` already handles Vite's ``vite:preloadError`` event, but that
 * fires only for the *preload* path; an import that rejects at navigation time
 * (the common case — clicking a menu item) does NOT emit it, so those slipped
 * through to the boundary. This closes that gap at the source: on a chunk-load
 * failure, reload once to pick up the new bundle.
 *
 * Guarded through ``sessionStorage`` so a genuine asset outage (the file is
 * really gone, not just renamed) surfaces as an error after one attempt instead
 * of a reload loop — same contract as the ``vite:preloadError`` handler, and
 * the same key, so the two paths share one budget.
 */
const RELOAD_KEY = 'nodus-chunk-reload-at';
const RELOAD_WINDOW_MS = 30_000;

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    // Safari / Firefox phrasings for the same missing-module failure.
    /Importing a module script failed/i.test(message)
  );
}

/**
 * The recovery logic, exported for testing.
 *
 * On a chunk-load failure: reload once (guarded) and return a never-resolving
 * promise so Suspense keeps showing the fallback until the document is replaced.
 * On anything else — including a second chunk failure inside the guard window —
 * re-throw so the error reaches the ErrorBoundary instead of looping the tab.
 */
export function recoverChunkLoad<T>(factory: () => Promise<T>): Promise<T> {
  return factory().catch((error: unknown) => {
    if (!isChunkLoadError(error)) throw error;

    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < RELOAD_WINDOW_MS) throw error;

    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    window.location.reload();

    return new Promise<T>(() => {}); // never resolves; the reload takes over
  });
}

export function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(() => recoverChunkLoad(factory));
}
